import { Grid, Card, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FaRegEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  ClosureDTO,
  Field,
  FieldTypeEnum,
  getAllClosureCustomFields,
  getClosureById,
} from 'wisestep-api';
import {
  formatLocation,
  getFormatDate,
  WsButton,
  WsTypoColors,
  WsTypography,
  activityFormattedDate,
  Item,
  WsTypoVariants,
} from 'wisestep-base';

import { failed } from '../../../../src/utils/NotificationMessages';
import { useNotificationStore } from '../../../../src/utils/NotificationStore';
import { useWorkspaceContext } from '../../../context';
import { ClosureAction, useClosureAuthz } from '../../../hooks/useClosureAuthz';
import { getCurrencySymbol } from '../../../utils';
import { getFeeArrangementFees } from '../../clientDetails/components/contractArrangementsTab';
import { useJob } from '../../jobs/hooks';
import { isEligibleForWisestepCandidates } from '../helper/ClosureHelper';
import { ClosureDetailsDTO } from '../types';

const ClosureDetailsDescription = ({ dt, dd }: { dt: string; dd: string | number }) => (
  <>
    <Grid item xs={1.5}>
      <WsTypography color={WsTypoColors.SECONDARY}>{dt}</WsTypography>
    </Grid>
    <Grid item xs={2.5}>
      <WsTypography>{dd}</WsTypography>
    </Grid>
  </>
);

const ShowClosureCandidateDetails = ({
  closure,
  placementStatusReasonValue,
  showEditClosure,
  placementType,
}: {
  closure: ClosureDetailsDTO;
  placementStatusReasonValue: string;
  showEditClosure: boolean | string;
  placementType: string;
}) => {
  const workspace = useWorkspaceContext();
  const navigate = useNavigate();
  const [job] = useJob(closure.positionId ?? '');
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [closureCandidate, setClosureCandidate] = useState<ClosureDTO>();
  const [customFields, setCustomFields] = useState<Field[]>([]);
  const isAuthorizedEditClosure = useClosureAuthz(ClosureAction.EDIT_CLOSURE);

  useEffect(() => {
    (async () => {
      try {
        const response = await getClosureById(closure.id);
        setClosureCandidate(response.data);
      } catch (e) {
        setNotification(failed(`Failed to fetch company Information`));
      }
    })();
    (async () => {
      const { data } = await getAllClosureCustomFields();
      setCustomFields(data.filter((field) => field.visible));
    })();
  }, [closure.id, setNotification]);

  const getTempFeeArrangement = (closure: ClosureDetailsDTO): string => {
    if (closure.feeType === 'PERCENTAGE') {
      return `Permanent / ${closure.feeArrangementAmount}%`;
    } else {
      return `Permanent / ${closure.feeArrangementAmount}`;
    }
  };

  return (
    <Grid item xs={12} spacing={2} mt={3}>
      <Card elevation={0}>
        {showEditClosure && isAuthorizedEditClosure && (
          <Box px={2} pt={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <WsButton onClick={() => navigate('edit/0')} startIcon={<FaRegEdit />} variant="text">
              Edit Placement
            </WsButton>
          </Box>
        )}
        <Grid container component="dl" columnSpacing={2} rowSpacing={6} sx={{ px: 4, py: 1 }}>
          <ClosureDetailsDescription
            dt="Candidate Location"
            dd={formatLocation(closure.candidateLocation)}
          />
          <ClosureDetailsDescription dt="Candidate Email" dd={closure.candidate.email} />
          <ClosureDetailsDescription dt="Candidate Contact" dd={closure.candidate.phone} />
          <ClosureDetailsDescription dt="Job Title" dd={job?.title ?? ''} />
          <ClosureDetailsDescription
            dt="Job Location"
            dd={
              (job?.locations &&
                job.locations.map((l) => (
                  <>
                    {formatLocation(l)}
                    <br />
                  </>
                ))) as unknown as string
            }
          />
          <ClosureDetailsDescription dt="Organisation Entity" dd={closure.ourEntity?.Name ?? ''} />
          <ClosureDetailsDescription dt="Client Name" dd={closure.client.name} />
          <ClosureDetailsDescription
            dt="Client Recruiter"
            dd={
              closure.clientRecruiterOne
                ? closure.clientRecruiterOne.firstName + ' ' + closure.clientRecruiterOne.lastName
                : ''
            }
          />
          <ClosureDetailsDescription
            dt="Account Manager"
            dd={closure.accountManager ? JSON.parse(closure.accountManager)['text'] : ''}
          />
          <ClosureDetailsDescription
            dt="Team"
            dd={closure.team ? JSON.parse(closure.team)['text'] : ''}
          />
          <ClosureDetailsDescription
            dt="Placement Origin"
            dd={closure.placementOrigin?.originName ?? ''}
          />
          <ClosureDetailsDescription dt="Business Unit" dd={closure.businessUnitName ?? ''} />
          {placementType === 'PERMANENT' && (
            <ClosureDetailsDescription
              dt="Payment Terms"
              dd={`${closure.feeArrangement.paymentTerms} days`}
            />
          )}
          <ClosureDetailsDescription
            dt="Joining Date"
            dd={getFormatDate(String(closure.joiningDate))}
          />
          {placementType === 'CONTRACT' && (
            <>
              <ClosureDetailsDescription
                dt="End Date"
                dd={getFormatDate(String(closure.endDate))}
              />
              <ClosureDetailsDescription
                dt="Notice Period"
                dd={closure.noticePeriod ? `${closure.noticePeriod} days` : ''}
              />
              <ClosureDetailsDescription
                dt="Renewal Period"
                dd={closure.renewalPeriod ? `${closure.renewalPeriod} days` : ''}
              />
            </>
          )}
          {placementType === 'PERMANENT' && (
            <>
              <ClosureDetailsDescription
                dt="EG Days"
                dd={
                  closure.arrangementType === 'TEMP'
                    ? `${closure.employmentGuaranteeDays} Days`
                    : `${closure.feeArrangement.employmentGuarantee} ${closure.feeArrangement.empGuaranteePeriodUnit}`
                }
              />
              <ClosureDetailsDescription
                dt="EG End Date"
                dd={getFormatDate(String(closure.contractGuaranteeEndDate))}
              />
              <ClosureDetailsDescription
                dt="Compensation"
                dd={`${
                  closure.sellRateCurrency
                    ? getCurrencySymbol(closure.sellRateCurrency?.symbol)
                    : ''
                } ${closure.sellRateAmount} ${closure.sellRateFrequency}`}
              />
              <ClosureDetailsDescription
                dt="Fee Arrangement"
                dd={
                  closure.arrangementType !== 'TEMP'
                    ? `${closure.feeArrangement.contractType} / ${getFeeArrangementFees(
                        closure.feeArrangement
                      )}`
                    : getTempFeeArrangement(closure)
                }
              />
              <ClosureDetailsDescription
                dt="Commission Value"
                dd={`${
                  closure.sellRateCurrency
                    ? getCurrencySymbol(closure.sellRateCurrency?.symbol)
                    : ''
                } ${closure.commissionValue}`}
              />
            </>
          )}
          <ClosureDetailsDescription
            dt="Placement Status Reason"
            dd={`${placementStatusReasonValue === undefined ? '' : placementStatusReasonValue}`}
          />
          {customFields
            .filter((field) => field.type !== FieldTypeEnum.Tags)
            .map((field) => {
              const value = closureCandidate?.customFields?.[field.name];
              const text =
                field.type === FieldTypeEnum.Location && value
                  ? [value.city, value.state, value.country].toString() || value.continent
                  : field.type === FieldTypeEnum.Date && value
                  ? getFormatDate(value.toString())
                  : field.type === FieldTypeEnum.DateAndTime && value
                  ? activityFormattedDate(value.toString())
                  : field.type === FieldTypeEnum.Users
                  ? value?.[0]
                  : field.type === FieldTypeEnum.Dropdown && field.multiple
                  ? value?.join(', ')
                  : field.type === FieldTypeEnum.Checkbox && field.options.length === 0
                  ? value
                    ? 'Yes'
                    : 'No'
                  : value;
              return (
                <>
                  <ClosureDetailsDescription dt={field.label} dd={text ?? 'Not Available'} />
                </>
              );
            })}
          {closureCandidate?.description && (
            <Grid container item xs={12}>
              <Grid item xs={1.5}>
                <Item>
                  <WsTypography color={WsTypoColors.SECONDARY}>Description</WsTypography>
                </Item>
              </Grid>
              <Grid item xs={10.5}>
                <Item>
                  <WsTypography>{closureCandidate?.description}</WsTypography>
                </Item>
              </Grid>
            </Grid>
          )}
          {workspace.id && isEligibleForWisestepCandidates(workspace.id) && (
            <ClosureDetailsDescription
              dt="Wisestep Candidate"
              dd={`${closure.wisestepCandidate ? 'Yes' : 'No'}`}
            />
          )}
          {placementType === 'CONTRACT' && (
            <>
              <Grid item p={1} sm={11.5}>
                <WsTypography
                  variant={WsTypoVariants.TITLE}
                  color={WsTypoColors.SECONDARY}
                  bold={true}
                >
                  Compensation Details
                </WsTypography>
              </Grid>
              <ClosureDetailsDescription
                dt="Buy Rate"
                dd={`${
                  closure.buyRateCurrency ? getCurrencySymbol(closure.buyRateCurrency?.symbol) : ''
                } ${closure.buyRateAmount} ${closure.buyRateFrequency}`}
              />
              <>
                <Grid item xs={1.5}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Buy Rate Description </WsTypography>
                </Grid>
                <Grid item xs={6.5}>
                  <WsTypography>{closure.buyRateDescription}</WsTypography>
                </Grid>
              </>
              <ClosureDetailsDescription
                dt="Sell Rate"
                dd={`${
                  closure.sellRateCurrency
                    ? getCurrencySymbol(closure.sellRateCurrency?.symbol)
                    : ''
                } ${closure.sellRateAmount} ${closure.sellRateFrequency}`}
              />
              <>
                <Grid item xs={1.5}>
                  <WsTypography color={WsTypoColors.SECONDARY}>Sell Rate Description </WsTypography>
                </Grid>
                <Grid item xs={6.5}>
                  <WsTypography>{closure.sellRateDescription}</WsTypography>
                </Grid>
              </>
              {closure.overtimeApplicable && (
                <>
                  <ClosureDetailsDescription
                    dt="Overtime Buy Rate"
                    dd={`${
                      closure.overTimeBuyRateCurrency
                        ? getCurrencySymbol(closure.overTimeBuyRateCurrency?.symbol)
                        : ''
                    } ${closure.overTimeBuyRateAmount} ${closure.overTimeBuyRateFrequency}`}
                  />
                  <ClosureDetailsDescription
                    dt="Overtime Sell Rate"
                    dd={`${
                      closure.overTimeSellRateCurrency
                        ? getCurrencySymbol(closure.overTimeSellRateCurrency?.symbol)
                        : ''
                    } ${closure.overTimeSellRateAmount} ${closure.overTimeSellRateFrequency}`}
                  />
                </>
              )}
            </>
          )}
        </Grid>
      </Card>
    </Grid>
  );
};

export default ShowClosureCandidateDetails;
